import React, { createContext, useContext, useState } from 'react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import useLegalEntities, { LegalEntity } from 'modules/employees/hooks/useLegalEntities'

interface Props {
  children: React.ReactNode
  setLoading: (loading: boolean) => void
}

interface CachedData {
  legalEntities: LegalEntity[]
}

interface IValue {
  data: CachedData
  loading: boolean
}

const CachedDataContext = createContext<IValue | undefined>(undefined)

export const CachedDataProvider: React.FC<Props> = ({ children, setLoading }) => {
  const [data, setData] = useState<CachedData>({ legalEntities: [] })

  const { legalEntities, loading: loadingLegalEntities } = useLegalEntities()

  useUnsafeEffect(() => {
    setData((prev) => ({
      ...prev,
      legalEntities,
    }))
  }, [legalEntities])

  useUnsafeEffect(() => {
    setLoading(loadingLegalEntities)
  }, [loadingLegalEntities, setLoading])

  return (
    <CachedDataContext.Provider value={{ data, loading: loadingLegalEntities }}>
      {children}
    </CachedDataContext.Provider>
  )
}

export const useCachedData = () => {
  const context = useContext(CachedDataContext)
  if (context === undefined) {
    throw new Error('useCachedData must be used within a CachedDataProvider')
  }
  return context
}
